/**
 * Descripcion y ficha tecnica de un producto (ADM-02).
 *
 * La ficha tecnica se escribe como texto, una especificacion por linea con la
 * forma `Campo: valor`; la tienda la convierte en tabla
 * (`utilidades/producto.ts`). Es lo que se puede escribir en un cuadro de texto
 * sin aprender nada.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import { actualizarContenidoProducto } from '../../servicios/administracion.servicio';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';

export interface DatosContenido {
  descripcion: string | null;
  fichaTecnica: string | null;
}

export interface FormularioContenidoProps {
  productoId: number;
  inicial: DatosContenido;
  onGuardado: (datos: DatosContenido) => void;
}

export function FormularioContenido({ productoId, inicial, onGuardado }: FormularioContenidoProps) {
  const [descripcion, setDescripcion] = useState(inicial.descripcion ?? '');
  const [fichaTecnica, setFichaTecnica] = useState(inicial.fichaTecnica ?? '');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  async function guardar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setGuardando(true);
    setFalla(null);

    const datos: DatosContenido = {
      descripcion: descripcion.trim().length > 0 ? descripcion.trim() : null,
      fichaTecnica: fichaTecnica.trim().length > 0 ? fichaTecnica.trim() : null,
    };

    try {
      await actualizarContenidoProducto(productoId, datos);
      onGuardado(datos);
      setGuardado(true);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={(evento) => {
        void guardar(evento);
      }}
    >
      {falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{falla}</span>
        </p>
      ) : null}

      <div className="campo">
        <label htmlFor="prod-descripcion">Descripción</label>
        <textarea
          id="prod-descripcion"
          rows={5}
          value={descripcion}
          onChange={(evento) => {
            setDescripcion(evento.target.value);
            setGuardado(false);
          }}
        />
      </div>

      <div className="campo">
        <label htmlFor="prod-ficha">Ficha técnica</label>
        <textarea
          id="prod-ficha"
          rows={5}
          value={fichaTecnica}
          placeholder={'Espesor: 3 mm\nMaterial: Neopreno'}
          onChange={(evento) => {
            setFichaTecnica(evento.target.value);
            setGuardado(false);
          }}
        />
        <p className="ayuda-campo">Una especificación por línea, como «Campo: valor».</p>
      </div>

      <div className="admin-acciones">
        <button type="submit" className="btn btn-primario" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {guardado ? (
          <span className="etiqueta-ok" role="status">
            <Icono nombre="check" />
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}
